import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { appEnv } from '../../app.config';
import { ResponseModel } from '../models/response.model';

/**
 * Service for Vehicle type-related api calls.
 *
 * Note: Placing this in core module because it has possibilites to be used in multiple modules.
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleService {

  // Only the unfiltered (no driverId) list is cached — it's the full vehicle
  // type list used by several Settings tabs and rarely changes. Driver-scoped
  // lookups vary per call and are never cached.
  private _allTypesCache$: Observable<ResponseModel> | null = null;

  constructor(private httpClient: HttpClient) { }

  /**
   * Api for getting the list of vehicle types.
   * @returns Observale<any>
   */
  public getVehicleTypes(driverId?: number, includeInactive: boolean = false): Observable<ResponseModel> {
    if (driverId && driverId > 0) {
      return this.httpClient.get<ResponseModel>(`${appEnv.api}vehicle-type?driver_id=${driverId}`);
    }
    // "Show deactivated" is a Settings > Vehicle Types-only view - never cached alongside
    // the default (active-only) list that the rest of the app relies on.
    if (includeInactive) {
      return this.httpClient.get<ResponseModel>(`${appEnv.api}vehicle-type?include_inactive=1`);
    }
    if (!this._allTypesCache$) {
      this._allTypesCache$ = this.httpClient.get<ResponseModel>(`${appEnv.api}vehicle-type`).pipe(shareReplay(1));
    }
    return this._allTypesCache$;
  }

  public storeUpdate (id: number = 0, data: Array<any>): Observable<ResponseModel> {
    return this.httpClient.post<ResponseModel>(`${appEnv.api}settings/vehicle-type/store${id ? `?id=${id}` : ''}`, data)
      .pipe(tap(() => this._allTypesCache$ = null));
  }

  public delete (id: number = 0): Observable<ResponseModel> {
    return this.httpClient.delete<ResponseModel>(`${appEnv.api}settings/vehicle-type/record/${id}`)
      .pipe(tap(() => this._allTypesCache$ = null));
  }

  public reOrderVehicleList (data: Array<{id: number, order: number}>) {
    return this.httpClient.post<ResponseModel>(`${appEnv.api}settings/vehicle-type/re-order`, {orders: data});
  }

  public deactivate (id: number): Observable<ResponseModel> {
    return this.httpClient.patch<ResponseModel>(`${appEnv.api}settings/vehicle-type/${id}/deactivate`, {})
      .pipe(tap(() => this._allTypesCache$ = null));
  }

  public activate (id: number): Observable<ResponseModel> {
    return this.httpClient.patch<ResponseModel>(`${appEnv.api}settings/vehicle-type/${id}/activate`, {})
      .pipe(tap(() => this._allTypesCache$ = null));
  }
}
